import { useState, useEffect } from "react";
import { Loader2, FileText, ExternalLink, FileSignature, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { PaymentSummary } from "@/components/billing/PortalBilling";

const B = process.env.REACT_APP_BACKEND_URL;
const KIND = { package: "Proposal", audit: "Website audit", proposal: "Growth plan", invoice: "Invoice", statement: "Statement", upload: "Document" };
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const A = ({ href, children, testId, primary }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testId}
    className={primary ? "inline-flex items-center gap-1 text-sm font-bold bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-lg" : "inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline"}>{children}</a>
);

const PackageLinks = ({ d }) => (
  <div className="flex flex-wrap items-center gap-3 mt-2">
    <A href={`${B}/api/documents/${d.id}/file?part=audit`} testId={`my-report-audit-${d.id}`}><FileText className="w-3.5 h-3.5" />Audit report</A>
    <A href={`${B}/api/documents/${d.id}/file`} testId={`my-report-plan-${d.id}`}><FileText className="w-3.5 h-3.5" />Growth plan</A>
    {d.share_token && <>
      <A href={`/invoice/${d.share_token}`} testId={`my-report-invoice-${d.id}`}><ExternalLink className="w-3.5 h-3.5" />Invoice {d.invoice?.invoice_number} · {money(d.invoice?.total)}</A>
      {d.signed ? <span className="text-sm text-green-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Signed{d.invoice?.balance >= 0.5 ? ` · ${money(d.invoice.balance)} due` : " · paid"}</span>
        : <A primary href={`/p/${d.share_token}#packet`} testId={`my-report-sign-${d.id}`}><FileSignature className="w-4 h-4" />Review, sign & pay</A>}
      {d.signed && d.invoice?.balance >= 0.5 && <A primary href={`/invoice/${d.share_token}`} testId={`my-report-pay-${d.id}`}>Pay now</A>}
    </>}
  </div>
);

export const MyReportsTab = () => {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get("/api/customer/documents").then(r => setDocs(r.data.filter(d => !["package", "quote", "invoice"].includes(d.kind)))).catch(() => setDocs(false)); }, []);
  if (docs === false) return <p className="text-sm text-slate-500" data-testid="my-reports-error">Reports aren't available right now.</p>;
  if (!docs) return <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto my-10" />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm max-w-3xl" data-testid="my-reports">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />Reports</h2>
      <p className="text-sm text-slate-500 mb-4">Your payment summary, statements, audits and documents. Quotes are under Quotes and invoices under Billing.</p>
      <PaymentSummary />
      {docs.length === 0 ? <p className="text-sm text-slate-400 text-center py-8" data-testid="my-reports-empty">No reports yet — they'll appear here when your team sends one.</p> : (
        <div className="divide-y divide-slate-100">{docs.map(d => (
          <div key={d.id} className="py-4" data-testid={`my-report-${d.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="font-semibold text-slate-900">{d.title}{d.mode === "draft" && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Draft</span>}</p>
                <p className="text-xs text-slate-500">{KIND[d.kind]} · {d.created_at.slice(0, 10)}{d.sent_by_name ? ` · from ${d.sent_by_name}` : ""}</p>
                {d.message && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{d.message}</p>}
              </div>
              {d.kind !== "package" && <A href={`${B}/api/documents/${d.id}/file`} testId={`my-report-open-${d.id}`}>Open <ExternalLink className="w-3.5 h-3.5" /></A>}
            </div>
            {d.kind === "package" && <PackageLinks d={d} />}
          </div>
        ))}</div>
      )}
    </div>
  );
};
